import type { ColumnDef } from "@tanstack/react-table";
import { collection, getDocs } from "firebase/firestore";
import { MoreHorizontal, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { ListSearch, ListToolbar } from "@/components/ui/list-toolbar";
import { PageHeader } from "@/components/ui/page-header";

import { db } from "../../../api/firebaseApi";
import { AdjustCreditsModal } from "../../../components/admin/AdjustCreditsModal";
import { SubscriptionTag } from "../../../components/common/SubscriptionTag";
import { DATABASE } from "../../../types/tables-data";

interface Customer {
  id: string;
  email: string;
  practiceName: string;
  created_at: string;
  subscriptionStatus: string;
  case_credits: number;
}

export const AdminCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, DATABASE.CUSTOMERS));
      setCustomers(
        querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        })) as Customer[]
      );
    } catch (error) {
      toast.error("Error fetching customers", {
        description: "Please try again"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (customer) =>
        customer.email?.toLowerCase().includes(q) ||
        customer.practiceName?.toLowerCase().includes(q)
    );
  }, [customers, searchQuery]);

  const columns: ColumnDef<Customer, unknown>[] = [
    {
      id: "practice",
      header: "Practice Name",
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-base text-fg">
            {row.original.practiceName}
          </span>
          {row.original.email ? (
            <span className="truncate text-sm text-fg-3">
              {row.original.email}
            </span>
          ) : null}
        </div>
      )
    },
    {
      id: "subscription",
      header: "Subscription",
      cell: ({ row }) => (
        <SubscriptionTag status={row.original.subscriptionStatus || "Active"} />
      )
    },
    {
      id: "credits",
      header: "Credit Balance",
      cell: ({ row }) => (
        <span className="text-base text-fg-2 tabular-nums">
          {row.original.case_credits || 0}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Options">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => {
                  setSelectedCustomer(row.original);
                  setShowCreditsModal(true);
                }}
              >
                Adjust Credits
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader title="Customers" count={customers.length || null} />
        <ListToolbar>
          <ListSearch
            aria-label="Search customers"
            placeholder="Search Customers"
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
        </ListToolbar>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {!isLoading && visible.length === 0 ? (
          <EmptyState
            icon={Users}
            title={customers.length ? "No Matching Customers" : "No Customers"}
            className="py-16"
          />
        ) : (
          <DataTable
            data={visible}
            columns={columns}
            rowHeight="double"
            getRowId={(c) => c.id}
            isLoading={isLoading}
            className="overflow-visible"
          />
        )}
      </div>
      {selectedCustomer && (
        <AdjustCreditsModal
          isOpen={showCreditsModal}
          onClose={() => {
            setShowCreditsModal(false);
            setSelectedCustomer(null);
          }}
          customer={selectedCustomer}
          onSuccess={fetchCustomers}
        />
      )}
    </div>
  );
};
